import { Box, Button, Drawer, IconButton, Typography } from "@mui/material"
import { useTranslation } from "react-i18next"
import { ArrowBack } from "../../../../components/arrowBack"
import { ReportSignatureProps } from "./types"
import { useQuestionnaireContext } from "../../pages/detail/contextProvider"
import { useCallback, useEffect, useState } from "react"
import { useStyles } from "./styles"
import { CustomPaper } from "../../../../components/CustomPaper"
import { icons } from "../../../../utils/icons"
import { SignatureItem } from "./componetns/signatureItem"

const defaultSignature = {
    id: Date.now(),
    requeired: false,
    description: '',
    addComment: false,
    reqiredComment: false
}

const ReportSignature = (props: ReportSignatureProps) => {
    const { t } = useTranslation()
    const { setModal, closeModal } = useQuestionnaireContext()
    const styles = useStyles()
    const [signatures, setSignatures] = useState([defaultSignature])
    const [errors, setErrors] = useState<Record<number, boolean>>({})

    const handleChange = useCallback(
        (id: number, field: keyof typeof defaultSignature, value: any) => {
            setSignatures((prev) =>
                prev.map((item, i) => {
                    if (item.id !== id) return item;

                    if (field === "addComment" && value === false) {
                        return { ...item, addComment: false, reqiredComment: false };
                    }

                    if (field === "requeired" && value === false) {
                        return { ...item, requeired: false, reqiredComment: false };
                    }

                    if (field === "reqiredComment" && value === true) {
                        return { ...item, requeired: true, reqiredComment: true };
                    }

                    return { ...item, [field]: value };
                })
            );

            if (field === "description") {
                setErrors((prev) => ({
                    ...prev,
                    [id]: value.trim() === "",
                }));
            }
        },
        []
    );


    const returnConfig = () => {
        setModal(prev => ({
            ...prev,
            open: true,
            title: t('general.goBackToConfig'),
            description: t('general.changeWillBeDiscarded'),
            confirm: () => {
                if (props.closeSignature) {
                    //returnConfig()
                    props.closeSignature()
                    setSignatures(props.data ?? [defaultSignature])
                }
                closeModal()
            },
            cancel: () => {
                closeModal()
            }
        }))
    }

    const handleAddSignature = useCallback(() => {
        setSignatures((prev) => [
            ...prev,
            { ...defaultSignature, id: Date.now() },
        ]);
    }, []);

    const deleteSignature = useCallback((id: number) => {
        setSignatures(prev => prev.filter(signature => signature.id !== id))
        setErrors((prev) => {
            const copy = { ...prev }
            delete copy[id]
            return copy
        })
    }, [])

    const handleSave = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()

        const validation: Record<number, boolean> = {}
        let hasError = false
        for (const sig of signatures) {
            const isError = !sig.description.trim()
            validation[sig.id] = isError
            if (isError) hasError = true
        }
        setErrors(validation)

        if (hasError) {
            setModal(prev => ({
                ...prev,
                open: true,
                title: t('Questionarie.signaturesWithoutDescription'),
                description: '',
                confirm: () => {
                    closeModal()
                },
                cancel: () => {
                    closeModal()
                }
            }))
            return
        }

        if (props.saveSignature) {
            props.saveSignature({ data: signatures })
        }
    }

    useEffect(() => {
        setSignatures(props.data ?? [defaultSignature])
        setErrors((props.data ?? [defaultSignature]).map(() => false))
    }, [props.data])
    return (
        <Drawer {...props} anchor="right">
            <Box sx={styles.container}>
                <ArrowBack
                    arrowProps={{
                        onClick: () => {
                            returnConfig()
                        },
                        sx: {
                            padding: '10px 20px 10px 0px',
                        }
                    }}
                    label={t('general.returnQuestionnaireConfig')}
                    boxProps={{
                        sx: {
                            marginLeft: '-20px'
                        }
                    }}
                />

                <Typography sx={styles.title} className="big">{t('Questionarie.reportWithSignature')}</Typography>

                <CustomPaper elevation={2} sx={styles.addContainer}>
                    <Typography sx={styles.title}>{t('Questionarie.fields.addSignature')}</Typography>

                    <IconButton sx={styles.add} onClick={handleAddSignature}>{icons("add")}</IconButton>
                </CustomPaper>

                {signatures.map((sig, index) => (
                    <SignatureItem
                        key={sig.id}
                        index={index}
                        data={sig}
                        onChange={handleChange}
                        onRemove={deleteSignature}
                        haveDeleteButton={signatures.length > 1}
                        errorDescription={errors[sig.id] ?? false}
                    />
                ))}

                <Box sx={styles.footer}>
                    <Button onClick={handleSave} variant="contained">{t('general.save')}</Button>

                    <Button onClick={() => { returnConfig() }}>{t('general.cancel')}</Button>
                </Box>
            </Box>
        </Drawer>
    )
}

export { ReportSignature }